import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NmsDialogService } from "@nms-angular-toolkit/nms-dialog";
import { DropdownMenu, DropdownShortcut } from "@nms-angular-toolkit/nms-dropdown";
import { NmsDropdownMenuComponent } from "@nms-angular-toolkit/nms-dropdown-menu";
import {
    ColumnConfig,
    FilterColumnParameters,
    FilterOperator,
    NmsTableComponent,
    PaginatedResponse,
    PagingFilterType,
    RequestConfig,
    RequestFilter,
    SortConfig,
    TableDataRuleType
} from "@nms-angular-toolkit/nms-table";
import { OnuView } from "@nms-ng1/features/gpon-onus/gpon-onus-models";
import { DeviceBriefingComponent } from "@nms-ng2/app/shared/components/elements/device-briefing/device-briefing.component";
import { NmsAutoPollingService } from "@nms-ng2/app/shared/components/elements/nms-auto-updater/nms-auto-polling.service";
import { UpdateDetail } from "@nms-ng2/app/shared/components/elements/nms-auto-updater/nms-auto-updater.component";
import { NmsToastrService } from "@nms-ng2/app/shared/components/elements/nms-toastr/nms-toastr.service";
import { WarningSettings } from "@nms-ng2/app/shared/components/elements/nms-warning/nms-warning.models";
import { CUSTOM_SVG } from "@nms-ng2/app/shared/constants/svg";
import { DevicesActionsService } from "@nms-ng2/app/shared/services/actions/devices-actions.service";
import { DevicePollingVerifierService } from "@nms-ng2/app/shared/services/polling/device-polling-verifier.service";
import { RefreshIntervalService } from "@nms-ng2/app/shared/services/refresh-interval/refresh-interval.service";
import { SvgHandlerService } from "@nms-ng2/app/shared/services/svg/svg-handler.service";
import { TableFilterService } from "@nms-ng2/app/shared/services/table-filter/table-filter-service";
import {
    ANGULARJS_ROOTSCOPE,
    ANGULARJS_SCOPE,
    ANGULARJS_TRANSLATE,
    DEVICE_DROPDOWN_MODEL_SERVICE,
    DEVICE_DROPDOWN_SHORTCUTS_SERVICE,
    FILTER,
    GPON_ONUS_STATUS_CONFIG,
    LABEL_COLOR_SERVICE,
    MONITORING_SERVICE,
    STATE,
    TEMPLATE_APPLICATION_ONE_STEP_SERVICE,
    TEMPLATE_TYPE
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { ZabbixWarningService } from "@nms-ng2/app/shared/services/zabbix/zabbix-warning.service";
import { Observable, Subscription } from "rxjs";
import { CpeDropdownService } from "../cpes/cpe-dropdown.service";
import { Device } from "../device-backup/device-backup.models";
import { GponOnusOneStepTemplateApplicationService } from "./gpon-onus-one-step-template-application.service";
import {
    FIELD_URL_MAP,
} from "./gpon-onus.constants";
import { DeviceDropdownShortcut, GponOnusField, GponOnuStatus, Ipv4Mode, SnmpMode } from "./gpon-onus.models";
import { PollingRequestClient } from "@nms-ng2/app/shared/models/polling-request-client";

/**
 * Componente responsável pela renderização da tabela paginada de onus.
 */
@Component({
    selector: "gpon-onus",
    templateUrl: "./gpon-onus.component.html",
    styleUrls: ["./gpon-onus.component.scss"],
    providers: [NmsAutoPollingService]
})
export class GponOnusComponent implements OnInit, AfterViewInit, OnDestroy {

    private readonly baseUrl: string = "/onus/search-all";
    private readonly baseUrlSingleDeviceContext: string = "/onus/search";
    private readonly zabbixUrlPlaceholder: string = "${zabbixUrl}"; // nosonar
    private readonly secondaryColor: string = "#337Ab7";

    deviceBriefing: Device;
    isSingleDeviceContext = false;
    deviceResourceId: number;
    retrieveDataButtonTranslationKey: string;
    updateDataLabelTranslationKey: string;
    retrieveMessageTranslationKey: string;
    warningSettings: WarningSettings;

    @ViewChild(NmsTableComponent)
    nmsTableComponent: NmsTableComponent;

    @ViewChild(DeviceBriefingComponent)
    deviceBriefingComponent: DeviceBriefingComponent;

    autoUpdateInterval: number;
    gponOnusProvider;
    monitoringSettings: any;
    hasSummaryAccess: boolean;
    availableFeaturesByDevice = [];
    nmsTableChangedSubscription: Subscription;
    nmsTableRulesRemovedSubscription: Subscription;
    deviceInfoSubscription: Subscription;

    columns: ColumnConfig[] = [
        {
            field: GponOnusField.DeviceName,
            label: "Device",
            optionalColumn: { type: "dropdown" },
            width: "200px",
            alwaysVisible: true,
            unsearchable: false
        },
        { field: GponOnusField.PortViewName, label: "Port", width: "120px", alwaysVisible: true },
        {
            field: GponOnusField.OnuId,
            label: "ID",
            optionalColumn: {
                type: "dropdown",
                defaultAction: (gpon) => {
                    this.openDeviceOnOnu(gpon);
                }
            },
            alwaysVisible: true
        },
        {
            field: GponOnusField.Status,
            label: "Status",
            availableValues: [
                { value: GponOnuStatus.NORMAL, label: this.translate.instant("status.normal") },
                { value: GponOnuStatus.MINOR_FAULT, label: this.translate.instant("status.minorFault") },
                { value: GponOnuStatus.MAJOR_FAULT, label: this.translate.instant("status.majorFault") },
                { value: GponOnuStatus.CRITICAL_FAULT, label: this.translate.instant("status.criticalFault") },
                { value: GponOnuStatus.UNKNOWN, label: this.translate.instant("status.unknown") },
                { value: GponOnuStatus.DISCOVERED, label: this.translate.instant("status.discovered") }
            ],
            optionalColumn: {
                type: "status",
                convert: (status) => {
                    const { translationKey, bgColor } = this.gponOnuStatusConfig[status.trim().toUpperCase()];
                    const color = this.labelColorService.getBestColorContrastForForeground(bgColor);
                    const label = this.translate.instant(translationKey);

                    return { label, bgColor, color };
                }
            }
        },
        {
            field: GponOnusField.RxPower,
            label: "Rx Power(dBm)",
            width: "160px",
            optionalColumn: {
                type: "text",
                buildLink: this.buildZabbixLink.bind(this)
            }
        },
        {
            field: GponOnusField.TxPower,
            label: "Tx Power(dBm)",
            width: "160px",
            optionalColumn: {
                type: "text",
                buildLink: this.buildZabbixLink.bind(this)
            }
        },
        { field: GponOnusField.Alarms, label: "Alarms", width: "110px"},
        { field: GponOnusField.AlarmsTimes, label: "Alarms Times", width: "160px" },
        {
            field: GponOnusField.SerialNumber,
            label: "Serial Number",
            width: "160px",
            optionalColumn: {
                type: "dropdown",
                defaultAction: (gpon) => {
                    this.openDeviceOnOnu(gpon);
                }
            }
        },
        { field: GponOnusField.Password, label: "Password", show: false, width: "120px" },
        { field: GponOnusField.VendorId, label: "Vendor ID", show: false, width: "125px" },
        { field: GponOnusField.EquipmentId, label: "Equipment ID", show: false, width: "145px" },
        { field: GponOnusField.OnuName, label: "Name", width: "214px" },
        { field: GponOnusField.OperationalState, label: "Oper.State", show: false, width: "130px" },
        { field: GponOnusField.PrimaryStatus, label: "Primary Status", show: false, width: "160px" },
        { field: GponOnusField.Distance, label: "Distance(km)", show: false, width: "145px" },
        { field: GponOnusField.Ipv4Address, label: "IPv4 Addr.", width: "145px" },
        { field: GponOnusField.CwmpCpeIp, label: "TR-069 Mgmt IP", width: "165px" },
        { field: GponOnusField.CwmpCpeLastInform, label: "TR-069 Last Inform", isDateFilter: true, width: "185px" },
        { field: GponOnusField.Tr069AcsProfile, label: "TR-069 Profile", show: false, width: "155px" },
        { field: GponOnusField.Ipv4DefaultGateway, label: "IPv4 default gateway", show: false, width: "200px" },
        {
            field: GponOnusField.Ipv4Mode,
            label: "IPv4 mode",
            width: "155px",
            availableValues: [
                { value: Ipv4Mode.DHCP, label: this.translate.instant("gpon.onus.ipv4.mode.dhcp") },
                { value: Ipv4Mode.STATIC, label: this.translate.instant("gpon.onus.ipv4.mode.static") },
                { value: Ipv4Mode.NOT_CONFIGURED, label: this.translate.instant("gpon.onus.ipv4.mode.notConfigured") }
            ],
            show: false
        },
        { field: GponOnusField.Ipv4Vlan, label: "IPV4 VLAN", width: "130px" },
        { field: GponOnusField.RgProfile, label: "RG Profile", show: false, width: "130px" },
        {
            field: GponOnusField.Snmp,
            label: "SNMP",
            availableValues: [
                { value: SnmpMode.ALL, label: this.translate.instant("gpon.onus.snmp.mode.all") },
                { value: SnmpMode.PROFILE, label: this.translate.instant("gpon.onus.snmp.mode.profile") },
                { value: SnmpMode.DISABLED, label: this.translate.instant("gpon.onus.snmp.mode.disabled") }
            ],
            show: false
        },
        { field: GponOnusField.UpstreamFec, label: "Upstream-FEC", show: false, width: "150px" },
        { field: GponOnusField.Uptime, label: "Uptime", minWidth: "118px", show: false, width: "115px"},
        { field: GponOnusField.LastSeenOnline, label: "Last Seen Online", show: false, width: "170px" },
        { field: GponOnusField.Version, label: "Version", minWidth: "138px", show: false },
        { field: GponOnusField.AutoProvisioned, label: "Auto Provisioned", show: false, width: "175px" },
        { field: GponOnusField.LineProfile, label: "Line Profile", width: "140px" },
        { field: GponOnusField.ServiceProfile, label: "Service Profile", show: false, width: "155px" },
        { field: GponOnusField.ServiceVlans, label: "Service VLANs", width: "155px" },
        { field: GponOnusField.FixedBandwidth, label: "Fixed BW(kbit/s)", width: "165px" },
        { field: GponOnusField.AssuredFixedBandwidth, label: "Assured+Fixed BW", width: "180px" },
        { field: GponOnusField.ActiveFwFormatted, label: "Active FW", width: "240px" },
        { field: GponOnusField.StandbyFwFormatted, label: "Standby FW", width: "240px", show: false },
        { field: GponOnusField.SoftwareDownloadState, label: "SW Download State", show: false, width: "190px" },
        { field: GponOnusField.LastUpdated, label: "Last Update by Device", isDateFilter: true, show: false, width: "208px" },
        { field: GponOnusField.Eth1Link, label: "Eth 1 Link", width: "125px" },
        {
            field: GponOnusField.Eth1InRate,
            label: "Eth 1 In Rate",
            width: "140px",
            optionalColumn: {
                type: "text",
                buildLink: this.resolveEthAndBuildZabbixLink.bind(this)
            }
        },
        {
            field: GponOnusField.Eth1OutRate,
            label: "Eth 1 Out Rate",
            width: "155px",
            optionalColumn: {
                type: "text",
                buildLink: this.resolveEthAndBuildZabbixLink.bind(this)
            }
        },
        { field: GponOnusField.Eth2Link, label: "Eth 2 Link", show: false, width: "120px" },
        {
            field: GponOnusField.Eth2InRate,
            label: "Eth 2 In Rate",
            width: "140px",
            optionalColumn: {
                type: "text",
                buildLink: this.resolveEthAndBuildZabbixLink.bind(this)
            },
            show: false
        },
        {
            field: GponOnusField.Eth2OutRate,
            label: "Eth 2 Out Rate",
            width: "155px",
            optionalColumn: {
                type: "text",
                buildLink: this.resolveEthAndBuildZabbixLink.bind(this)
            },
            show: false
        },
        { field: GponOnusField.Eth3Link, label: "Eth 3 Link", show: false, width: "130px" },
        {
            field: GponOnusField.Eth3InRate,
            label: "Eth 3 In Rate",
            width: "150px",
            optionalColumn: {
                type: "text",
                buildLink: this.resolveEthAndBuildZabbixLink.bind(this)
            },
            show: false
        },
        {
            field: GponOnusField.Eth3OutRate,
            label: "Eth 3 Out Rate",
            width: "155px",
            optionalColumn: {
                type: "text",
                buildLink: this.resolveEthAndBuildZabbixLink.bind(this)
            },
            show: false
        },
        { field: GponOnusField.Eth4Link, label: "Eth 4 Link", show: false, width: "120px" },
        {
            field: GponOnusField.Eth4InRate,
            label: "Eth 4 In Rate",
            width: "145px",
            optionalColumn: {
                type: "text",
                buildLink: this.resolveEthAndBuildZabbixLink.bind(this)
            },
            show: false
        },
        {
            field: GponOnusField.Eth4OutRate,
            label: "Eth 4 Out Rate",
            width: "155px",
            optionalColumn: {
                type: "text",
                buildLink: this.resolveEthAndBuildZabbixLink.bind(this)
            },
            show: false
        }
    ];
    dateFilterConfigs = {
        emptyDateFilterLabel: this.translate.instant("nms.table.view.date.filter.picker.emptyDateFilterLabel"),
        applyDateFilterButton: this.translate.instant("nms.table.view.date.filter.picker.applyDateFilterButton"),
        clearDateFilterButton: this.translate.instant("nms.table.view.date.filter.picker.clearDateFilterButton"),
        initialTimeDateFilterLabel: this.translate.instant("nms.table.view.date.filter.picker.initialTimeDateFilterLabel"),
        endTimeDateFilterLabel: this.translate.instant("nms.table.view.date.filter.picker.endTimeDateFilterLabel")
    };
    options: RequestConfig = {
        serverPagination: {
            baseUrl : ""
        }
    };
    operators: Array<FilterOperator> = [];
    dropdownMenus: Array<DropdownMenu>;
    deviceNameShortcuts: Array<DeviceDropdownShortcut> = [];
    initialSort: SortConfig[];
    loggedUser: string;
    columnsSettingsLabels: {};

    applyTemplateSVG: string;
    gponOnuDropdownMenu: string;
    keywordsTemplates: any[] = [];
    isLoading: boolean;

    constructor(@Inject(STATE) private readonly state: any,
                @Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
                @Inject(GPON_ONUS_STATUS_CONFIG) private readonly gponOnuStatusConfig: any,
                @Inject(MONITORING_SERVICE) private readonly monitoringService: any,
                @Inject(LABEL_COLOR_SERVICE) private readonly labelColorService: any,
                @Inject(DEVICE_DROPDOWN_MODEL_SERVICE) private readonly deviceDropdownModelService: any,
                private readonly refreshIntervalService: RefreshIntervalService,
                private readonly devicesActionService: DevicesActionsService,
                @Inject(DEVICE_DROPDOWN_SHORTCUTS_SERVICE) private readonly deviceDropDownShortcutsService: any,
                private readonly cpeDropdownService: CpeDropdownService,
                private readonly tableFilterService: TableFilterService,
                private readonly toastr: NmsToastrService,
                @Inject(ANGULARJS_SCOPE) private readonly scope: any,
                @Inject(TEMPLATE_APPLICATION_ONE_STEP_SERVICE) private readonly templateApplicationOneStepService,
                @Inject(TEMPLATE_TYPE) private readonly templateType,
                private readonly nmsDialogservice: NmsDialogService,
                private readonly gponOnusOneStepTemplateApplicationService: GponOnusOneStepTemplateApplicationService,
                @Inject(ANGULARJS_ROOTSCOPE) private readonly rootScope: any,
                private readonly cdr: ChangeDetectorRef,
                private readonly svgToImgUrlConverter: SvgHandlerService,
                private readonly nmsAutoPollingService: NmsAutoPollingService,
                @Inject(FILTER) private readonly filter: any,
                private readonly zabbixWarningService: ZabbixWarningService,
                private readonly pollingVerifierService: DevicePollingVerifierService) {
        this.loggedUser = this.rootScope.loggedUser;
        this.gponOnusProvider = this.retrieveGponOnus;
        this.refreshIntervalService.getRefreshInterval((interval) => {
            this.autoUpdateInterval = interval;
        });
        this.dropdownMenus = [
            {
                field: "deviceName",
                getShortcuts: (gpon) => this.getDeviceDropdownShortcuts(gpon)
            },
            {
                field: "onuId",
                getShortcuts: (gpon) => this.cpeDropdownService.getTR069Shortcuts(this.openDeviceOnOnu)
            },
            {
                field: "serialNumber",
                getShortcuts: (gpon) => this.cpeDropdownService.getTR069Shortcuts(this.openDeviceOnOnu)
            }
        ];

        this.operators = [
            {
                value: "AND",
                label: this.translate.instant("nms-table.filter.and"),
                selected: true
            },
            {
                value: "OR",
                label: this.translate.instant("nms-table.filter.or"),
                selected: false
            },
        ];

        this.initialSort = [
            {
                column: "deviceName",
                direction: "asc",
                label: "Device",
            },
            {
                column: "portViewName",
                direction: "asc",
                label: "Port",
            },
            {
                column: "onuId",
                direction: "asc",
                label: "ID",
            }
        ];

        this.columnsSettingsLabels = {
            iconTooltip: this.translate.instant("nms.table.organize.columns.tooltip"),
            noColumnFound: this.translate.instant("nms.table.organize.columns.search.not.found"),
            showHideColumns: this.translate.instant("nms.table.show.hide.columns.columns"),
            showHideColumnsTooltip: this.translate.instant("nms.table.show.hide.columns.tooltip"),
            searchColumnsPlaceholder: this.translate.instant("nms.table.organize.columns.search"),
            restoreOrdering: this.translate.instant("nms.table.organize.restore.ordering")
        };
        this.isLoading = true;
        this.applyTemplateSVG = this.svgToImgUrlConverter.getSvgWithCustomColor(CUSTOM_SVG.applyTemplate, "#337ab7");
        this.isSingleDeviceContext = this.state.params.device?.id ? true : false;
        this.options.serverPagination.baseUrl =
            this.isSingleDeviceContext ? `${this.baseUrlSingleDeviceContext}/${this.state.params.device.id}` : this.baseUrl;
        if (this.state.params?.filters) {
            this.options.serverPagination.filterParams = this.getRequestFilter();
        }

        const deviceNameColumn = this.columns.find(column => column.field === GponOnusField.DeviceName);
        deviceNameColumn.unsearchable = this.isSingleDeviceContext;
        this.gponOnuDropdownMenu =
            this.svgToImgUrlConverter.getSvgWithCustomColor(CUSTOM_SVG.gponOnuDropdownMenu, this.secondaryColor);

        this.handleDeviceInfoIfSingleDeviceContext();
    }

    async ngOnInit() {
        this.monitoringSettings = await this.monitoringService.getMonitoringSettings();
    }

    ngAfterViewInit(): void {
        this.retrieveGponOnus = this.retrieveGponOnus.bind(this);
        this.subscribeTableChange();
        this.subscribeTableRulesRemovedEvent();

        this.nmsTableComponent?.selection.changed.subscribe(() => {
            this.cdr.detectChanges();
        });
        /**
         * Devido a grande quantidade de ONUs e elementos na página
         * é necessário aguardar um pouco para esconder o loading,
         * para que os elementos estejam de fato carregados
         */
        setTimeout(() => this.isLoading = false, 500);
    }

    ngOnDestroy(): void {
        this.nmsTableChangedSubscription?.unsubscribe();
        this.nmsTableRulesRemovedSubscription?.unsubscribe();
        this.deviceInfoSubscription?.unsubscribe();
    }

    onApplyTemplate(templateSelected, dropDownMenu: NmsDropdownMenuComponent) {
        const template = {
            templateId: templateSelected.templateId,
            templateName: templateSelected.label,
            templateDescription: templateSelected.description
        }

        this.closeMenu(dropDownMenu);

        if (this.isValidSelectedOnus(this.nmsTableComponent.selection.selected)) {
            this.handlePonTemplateApplication(template);
        }
    }

    closeMenu(comp: NmsDropdownMenuComponent) {
        comp.close()
    }

    /**
     * Método responsável por escutar qualquer evento ocorrido na tabela, alteração nos filtros,
     * paginação, ordenação.
     */
    subscribeTableChange() {
        this.nmsTableChangedSubscription = this.nmsTableComponent.contentChangeEvent.subscribe((gponOnus) => {
            this.nmsTableComponent.applyLoading.next(true);
            const devicesIds = [...new Set(gponOnus.map(gpon => gpon.deviceId))];
            Promise.all(devicesIds.map(deviceId => {
                return this.getAvailableFeatures(deviceId).then((availableFeatures) => {
                    this.availableFeaturesByDevice.push({ deviceId, features: availableFeatures });
                });
            })).then(() => {
                gponOnus.forEach((gpon) => this.createDeviceNameShortcuts(gpon));
                this.nmsTableComponent.applyLoading.next(false);
                if (this.isSingleDeviceContext) {
                    this.deviceBriefingComponent.load();
                }
            });
        });
    }

    getPollingFeedbackMessage(): Array<UpdateDetail> {
        return this.nmsAutoPollingService.pollingFeedbackMessage;
    }

    notifyTableUpdateData({ data }: { data: PaginatedResponse }) {
        this.nmsTableComponent.updateData(data);

        if (this.isSingleDeviceContext) {
            this.deviceBriefingComponent.load();
            this.checkZabbixWarnings(this.deviceResourceId);
        }
    }

    getDeviceDropdownShortcuts(gpon): DropdownShortcut[] {
        if (this.deviceNameShortcuts.length > 0) {
            return this.deviceNameShortcuts.find(shortcut => shortcut.deviceId === gpon.deviceId).shortcuts;
        }

        return [];
    }

    retrieveGponOnus(): Observable<Object> {
        return this.isSingleDeviceContext
                ? this.nmsAutoPollingService.startDevicesPolling()
                : this.getTableData();
    }

    private getTableData(): Observable<Object> {
        return this.nmsTableComponent.getProvider();
    }

    /** Renderiza o auto-updater somente após a renderização da tabela */
    isTableLoaded() {
        return this.nmsTableComponent !== undefined;
    }

    processFilterChanges = (filterParameters: FilterColumnParameters) => {
        const requestFilters = this.tableFilterService.processFilterChanges(filterParameters);

        this.nmsTableComponent.filter(requestFilters);
    }

    getSelectedOnus() {
        return this.nmsTableComponent?.selection?.selected || [];
    }

    /**
     * Exibe mensagem de sucesso ao limpar todos os filtros/ordenações.
     */
    private subscribeTableRulesRemovedEvent() {
        this.nmsTableRulesRemovedSubscription = this.nmsTableComponent.tableRulesRemovedEvent
            .subscribe((ruleType: TableDataRuleType) => {
                let message = this.translate.instant("nms.table.view.rule.sort.remove.all");

                if (ruleType === "filter") {
                    message = this.translate.instant("nms.table.view.rule.filtering.remove.all");
                }

                this.toastr.success(message);
            });
    }

    private buildZabbixLink(row: Object, column: string): string {
        let url = "";

        if (this.monitoringSettings?.url && FIELD_URL_MAP[column]) {
            const value = row[FIELD_URL_MAP[column]] || "";
            url = value.replace(this.zabbixUrlPlaceholder, this.monitoringSettings.url);
        }

        return url;
    }

    private resolveEthAndBuildZabbixLink(row: Object, column: string): string {
        const value = row[column] || "";
        row[column] = this.filter("bytesFormatter")(value);

        return row[column].replace(this.zabbixUrlPlaceholder, this.monitoringSettings.url);
    }

    private getAvailableFeatures(deviceId) {
        return Promise.resolve(this.deviceDropdownModelService.getAvailableFeatures(deviceId));
    }

    private openDeviceOnOnu(gponOnu) {
        const pathKeys = this.createOnuInfoConfigPathKeys(gponOnu);
        const schemaJsonPath = "$.containers.config>interface.lists.gpon.template.lists.onu.template";
        const displayName = `${gponOnu.portViewName} onu ${gponOnu.onuId}`;

        const params: any = { displayName, path: schemaJsonPath, pathKeys };
        this.devicesActionService.goToDeviceConfigWithParams(gponOnu.deviceName, params);
    }

    private createOnuInfoConfigPathKeys(gponOnu) {
        const portIdValues = gponOnu.portId.split("/");
        const portId: any = { "chassis-id": portIdValues[0], "slot-id": portIdValues[1], "port-id": portIdValues[2] };
        const onuId: any = { id: gponOnu.onuId };

        return [portId, onuId];
    }

    private createDeviceNameShortcuts(gpon) {
        const device = this.deviceDropdownModelService.createModelFromGponOnu(gpon);
        const availableFeatures = this.availableFeaturesByDevice
            .find(availableFeatureByDevice => availableFeatureByDevice.deviceId === gpon.deviceId);
        const deviceShortcuts = this.deviceDropDownShortcutsService
            .getFeaturesShortcuts(availableFeatures.features, device.name, true);

        if (deviceShortcuts.length > 0) {
            deviceShortcuts[deviceShortcuts.length - 1].hasDivider = true;
        }
        const shortcutsByEquipmentType = this.deviceDropDownShortcutsService.getShortcutsByEquipmentType(device, true);
        const allDeviceShortcuts = [...deviceShortcuts, ...shortcutsByEquipmentType];
        this.deviceNameShortcuts.push({ deviceId: device.id, shortcuts: allDeviceShortcuts });
    }

    private handlePonTemplateApplication(template) {
        let deviceTemplateVariables;
        if (this.nmsTableComponent.selection.selected.length === 1) {
            const deviceOnu = this.nmsTableComponent.selection.selected[0] as OnuView;
            deviceTemplateVariables = this.gponOnusOneStepTemplateApplicationService
                .handleDataToSingleOnuApplication(deviceOnu);
        } else if (this.nmsTableComponent.selection.selected.length > 1) {
            deviceTemplateVariables = this.gponOnusOneStepTemplateApplicationService
                .handleDataToMultipleOnusApplication(this.nmsTableComponent.selection.selected);
        } else if (this.isSingleDeviceContext) {
            const firstRow = this.nmsTableComponent.tableDataSource.data[0] as unknown as OnuView;
            deviceTemplateVariables = this.gponOnusOneStepTemplateApplicationService
                .handleDataToSingleOnuApplication({deviceId: firstRow.deviceId} as OnuView);
        }

        this.templateApplicationOneStepService.tryToOpenTemplateApplicationModal(
            deviceTemplateVariables.device,
            deviceTemplateVariables.templateVariables,
            template,
            this.templateType.CLI
        );
    }

    private isValidSelectedOnus(onusSelection) {
        if (!this.isSingleDeviceContext) {
            const devicesNames = new Set(onusSelection.map((selected: OnuView) => selected.deviceName));
            if (devicesNames.size > 1) {
                this.nmsDialogservice.openDialog(
                    {
                        description: this.translate.instant("gpon.onus.tableActions.multipleDeviceSelectionError")
                    }
                );

                return false;
            }

            if (devicesNames.size === 0) {
                this.nmsDialogservice.openDialog(
                    {
                        description: this.translate.instant("gpon.onus.tableActions.deviceSelectionError")
                    }
                );

                return false;
            }
        }

        if (onusSelection.length !== 0 && !this.gponOnusOneStepTemplateApplicationService.isOnusSamePort(onusSelection)) {
            this.nmsDialogservice.openDialog(
                {
                    description: this.translate.instant("gpon.onus.tableActions.multiplePortSelectionError")
                }
            );

            return false;
        }

        return true;
    }

    private handleDeviceInfoIfSingleDeviceContext(): void {
        if (this.isSingleDeviceContext) {
            const device: Device = this.scope.$parent.$resolve.$stateParams.device;
            this.pollingVerifierService.checkIfFirstPollingHasFinished(device.id);

            this.deviceBriefing = device;
            this.deviceResourceId = device.id;
            const clientPolling = PollingRequestClient.INFO_CONFIG;

            this.setMessagesNmsAutoUpdater(device);
            this.nmsAutoPollingService.setup([this.deviceResourceId], clientPolling, this.getTableData.bind(this));
            this.checkZabbixWarnings(device.id)

        }
    }

    private async checkZabbixWarnings(resourceId: number) {
        this.warningSettings = await this.zabbixWarningService.getZabbixWarnings(resourceId);
        this.cdr.detectChanges();
    }

    private setMessagesNmsAutoUpdater(device: Device) {
        this.retrieveDataButtonTranslationKey = "nms.auto.updater.button.device.label";
        this.updateDataLabelTranslationKey = "device.polling.status.timeRemaining";
        this.retrieveMessageTranslationKey = "nms.auto.updater.updating.device.message";
    }

    private setLastPolligDuration(device: Device) {
        // TODO - Será implementado na TK-100538 desta US
    }

    private getRequestFilter() {
        const requestFilter : RequestFilter = {};
        Object.entries(this.state.params.filters).forEach(([field, value]) => {
            const filterValues = Array.isArray(value) ? value.map(String) : [String(value)];
            const pagingFilterType = PagingFilterType.CONTAINS
            requestFilter[field] = {
                pagingFilterType,
                filterValues
            };
        });

        return requestFilter;
    }

}
